import {AuditLogOptions, AuditLogType, IAuditService} from "../PA.Core.IF.Audit";
import {PAdbContext} from "../PAdbContext";
import {WB_AuditLogRow} from "../PAdbSchemaRows";
import {esWB_Audit_Log} from "../PAdbSchema";
import {PAusrContext} from "../PAusrContext";
import {PAEnvironment} from "../PAEnvironment";
import * as _ from 'lodash'
import {ESRecord} from "../lib/ESRecord";
import {ESDataStore} from "../lib/ESDataStore";
import {TextUtils} from "../Basic/Text";
import {IMessage, MessageType} from "../PA.Core.IF.Basic";
import {IwbAuditLog} from "../PAdbSchemaRowsIF";
import {DateUtils} from "../Basic/Dates";

export class AuditService
    implements IAuditService
{

    private _dbContext: PAdbContext

    private _WB_ReferenceType_ID: string
    private _WB_Reference_ID: number
    private _WB_JobProcess_ID: number
    private _WB_Job_Process_Queue_ID: number

    private _AuditLogRows: WB_AuditLogRow[]
    private _UsrContext: PAusrContext
    private _recordsToCreate: IwbAuditLog[];

    constructor(dbContext?: PAdbContext) {
        if (!dbContext) {
            this._dbContext = new PAdbContext();
        } else {
            this._dbContext = dbContext;
        }
        this._WB_ReferenceType_ID = ""
        this._WB_Reference_ID = 0
        this._AuditLogRows = []
        this._UsrContext = PAEnvironment.getValue(PAEnvironment.UserContext);
        this._recordsToCreate = []
    }

    setReference(WB_ReferenceType_ID: string, WB_Reference_ID: number): void {
        this._WB_ReferenceType_ID = WB_ReferenceType_ID
        this._WB_Reference_ID = WB_Reference_ID
    }

    setJobProcess(WB_JobProcess_ID: number) {
        this._WB_JobProcess_ID = WB_JobProcess_ID;
    }

    /** Sets the Queue record for audit entries referencing a queue item */
    setQueueRecord(WB_Job_Process_Queue_ID: number) {
        this._WB_Job_Process_Queue_ID = WB_Job_Process_Queue_ID;
    }

    async Log(sType: AuditLogType, pMessage: string, pOptions?: AuditLogOptions): Promise<void> {
        const opts = pOptions || {}

        const msgLines = TextUtils.splitConservingWords(pMessage, 1000)
        for (const sMessage of msgLines) {

            const newRecord = <WB_AuditLogRow> await this._dbContext.createRecordWithKey(esWB_Audit_Log)

            if (_.isEmpty(newRecord))
                return

            this.setupLogRecord(newRecord, sType, sMessage, opts)

            this._AuditLogRows.push(newRecord)
        }

    }

    LogSync(sType: AuditLogType, pMessage: string, pOptions?: AuditLogOptions) {
        const opts = pOptions || {};

        const msgLines = TextUtils.splitConservingWords(pMessage, 1000)
        _.forEach(msgLines, (sMessage) => {

            const newRecord: IwbAuditLog = {}

            this.setupLogRecord(newRecord, sType, sMessage, opts)

            this._recordsToCreate.push(newRecord);

        })

    };

    private setupLogRecord(newRecord: IwbAuditLog,
                           sType: AuditLogType,
                           sMessage: string,
                           pOptions?: AuditLogOptions){
        const opts = pOptions || {}

        newRecord.AUDITLOG_TYPE = sType || AuditLogType.Info
        newRecord.LOG_MESSAGE = sMessage
        newRecord.WB_REFERENCE_TYPE = this._WB_ReferenceType_ID
        newRecord.WB_REFERENCE_ID = this._WB_Reference_ID
        newRecord.WB_JOBPROCESS_ID = this._WB_JobProcess_ID ?? 0
        newRecord.WB_JOB_PROCESS_QUEUE_ID = this._WB_Job_Process_Queue_ID ?? 0
        if (!_.isUndefined(opts.WB_AUTHUSR_ID) && _.isNumber(opts.WB_AUTHUSR_ID)) {
            newRecord.WB_AUTHUSR_ID = opts.WB_AUTHUSR_ID
        } else {
            if (this._UsrContext && this._UsrContext.User)
                newRecord.WB_AUTHUSR_ID = this._UsrContext.User.cif.WB_AUTHUSR_ID
        }

        newRecord.WB_SESSION_ID = 0

        const createDt: Date = opts.Time || new Date()
        const aSplitDtTm = DateUtils.convertToSAPBWDateTimeSplit(createDt)
        newRecord.CREATED_DT = aSplitDtTm?.Dt || ''
        newRecord.CREATED_TM = aSplitDtTm?.Tm || ''
        newRecord.WB_CUST_ID = opts.WB_Cust_Id || 0
        newRecord.WB_PRDGRP_ID = opts.WB_PrdGrp_Id || 0
        newRecord.SOURCE_ID = opts.Source_Id || ''
        newRecord.SOURCE_INTEGRATION_ID = opts.Source_Integration_Id || ''
        newRecord.REFERENCE_AMT = opts.Reference_Amt || 0
        newRecord.WB_SECREF_ID = opts.WB_SecondaryReference_Id || 0
        newRecord.SECREF_ID = opts.SecondaryReference_Id || ''
        newRecord.PREVIOUS_VALUE = opts.PreviousValue || '';
        newRecord.NEW_VALUE = opts.NewValue || ''

    }

    LogMsgSync(msg: IMessage, pOptions?: AuditLogOptions): void {

        if(_.isEmpty(msg))
            return
        const opts = pOptions || {}
        opts.Time = msg.time

        let sType = AuditLogType.Info
        switch (msg.type){
            case MessageType.Error:
                sType = AuditLogType.Error
                break
            case MessageType.Warning:
                sType = AuditLogType.Warning
                break
        }
        let logTxt = msg.description
        if(_.isEmpty(logTxt))
            logTxt = msg.title

        this.LogSync(sType, logTxt, opts)

    }


    async Save(bUseBatch?: boolean): Promise<void> {
        if (_.isEmpty(this._dbContext)) {
            return
        }

        const auditLogRows: WB_AuditLogRow[] = this._AuditLogRows;
        this._AuditLogRows = []

        const recordsToCreate = this._recordsToCreate
        this._recordsToCreate = []

        const saveRecords: WB_AuditLogRow[] = _.clone(auditLogRows || [])

        try {

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bUseBatch) {
                saveFunction = this._dbContext.SaveBatch.bind(this._dbContext);
            } else {
                saveFunction = this._dbContext.Save.bind(this._dbContext);
            }

            if (recordsToCreate.length) { //This code will create bulk ids and use them to save bulk audit logs
                for(const rowProps of  recordsToCreate) {
                    const newRow =<WB_AuditLogRow> await this._dbContext.createRecordWithKey(esWB_Audit_Log)
                    const c = newRow.cif
                    _.forEach(rowProps, (value, idx) => {
                        c[idx] = value
                    })
                    this._dbContext.add(esWB_Audit_Log, newRow)
                    saveRecords.push(newRow);

                }
            }

            saveRecords.push(...auditLogRows)

            await saveFunction(esWB_Audit_Log, saveRecords)

        } catch (err) {
            // Restore rows to Save on error...
            this._AuditLogRows.push(...auditLogRows)
            this._recordsToCreate.push(...recordsToCreate)
        }

    }


}
